import { ReactNode } from 'react';
import { IconName } from '@frontend/icons';
import { useDebouncedValue } from '../../hooks';
import { Accordion } from '../accordion';
import { Button } from '../button';
import { ChecklistField, RadioField, SearchField, useFormField } from '../forms';
import { Text } from '../text';
import {
  accordionBodyStyle,
  accordionHeaderStyle,
  checklistOptionStyle,
  clearActionsRowStyle,
  emptyMessageStyle,
  headerIconStyle,
  scrollFieldStyle,
  selectActionsRowStyle,
  selectionBadgeStyle,
} from './accordion-multi-select.styles';

export interface AccordionMultiSelectItem {
  value: string;
  label: string;
}

export interface AccordionMultiSelectItemProps {
  title: string;
  iconName: IconName;
  items: AccordionMultiSelectItem[];
  fieldProps: any;
  hideSearch?: boolean;
  selectAllLabel?: string;
  clearLabel?: string;
  emptyMessage?: string;
  isSingleSelect?: boolean;
  hideClear?: boolean;
}

export interface AccordionMultiSelectProps {
  children: ReactNode;
  isMulti?: boolean;
}

const AccordionMultiSelectItem = ({
  title,
  iconName,
  items,
  fieldProps,
  hideSearch = false,
  selectAllLabel = 'Select All',
  clearLabel = 'Clear',
  emptyMessage = 'No results found',
  isSingleSelect = false,
  hideClear = false,
}: AccordionMultiSelectItemProps) => {
  const searchField = useFormField({ type: 'text' });
  const debouncedSearch = useDebouncedValue(searchField.value, 300);

  const selectedValues = isSingleSelect ? (fieldProps.value ? [fieldProps.value] : []) : fieldProps.value ?? [];

  const filteredItems = debouncedSearch.trim()
    ? items.filter((item) => item.label.toLowerCase().includes(debouncedSearch.toLowerCase()))
    : items;

  const isAllSelected =
    !isSingleSelect && filteredItems.length > 0 && filteredItems.every((item) => selectedValues.includes(item.value));
  const isAnySelected = filteredItems.some((item) => selectedValues.includes(item.value));
  const selectionCount = selectedValues.length;

  const handleSelectAll = () => {
    if (isSingleSelect) return;
    const newValues = filteredItems.map((item) => item.value).filter((v) => !selectedValues.includes(v));
    if (newValues.length > 0) {
      fieldProps.onChange({ name: fieldProps.name, value: newValues });
    }
  };

  const handleClear = () => {
    if (isSingleSelect) {
      fieldProps.onChange({ name: fieldProps.name, value: null });
    } else {
      const valuesToRemove = filteredItems
        .filter((item) => selectedValues.includes(item.value))
        .map((item) => item.value);
      fieldProps.onChange({ name: fieldProps.name, value: valuesToRemove });
    }
  };

  return (
    <Accordion.Item value={fieldProps.name}>
      <Accordion.Header className={accordionHeaderStyle}>
        <Accordion.Header.Icon title={title} icon={iconName} className={headerIconStyle} />
        {!isSingleSelect && selectionCount > 0 && <span className={selectionBadgeStyle}>{selectionCount}</span>}
      </Accordion.Header>
      <Accordion.Body className={accordionBodyStyle}>
        {!hideSearch && (
          <SearchField name={`${fieldProps.name}-search`} {...searchField} onKeyDown={(e) => e.stopPropagation()} />
        )}
        {!isSingleSelect && (
          <div className={selectActionsRowStyle}>
            <Button variant='tertiary' disabled={isAllSelected} onClick={handleSelectAll}>
              {selectAllLabel}
            </Button>
            <Button variant='tertiary' disabled={!isAnySelected} onClick={handleClear}>
              {clearLabel}
            </Button>
          </div>
        )}
        {isAnySelected && isSingleSelect && !hideClear && (
          <div className={clearActionsRowStyle}>
            <Button variant='tertiary' onClick={handleClear}>
              {clearLabel}
            </Button>
          </div>
        )}
        {!filteredItems.length ? (
          <Text className={emptyMessageStyle}>{emptyMessage}</Text>
        ) : isSingleSelect ? (
          <RadioField {...fieldProps} label='' showLabel={false} className={scrollFieldStyle}>
            {filteredItems.map((item) => (
              <RadioField.Radio key={item.value} value={item.value} className={checklistOptionStyle}>
                {item.label}
              </RadioField.Radio>
            ))}
          </RadioField>
        ) : (
          <ChecklistField {...fieldProps} label='' showLabel={false} className={scrollFieldStyle}>
            {filteredItems.map((item) => (
              <ChecklistField.Option key={item.value} name={item.value} className={checklistOptionStyle}>
                {item.label}
              </ChecklistField.Option>
            ))}
          </ChecklistField>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export const AccordionMultiSelect = ({ children, isMulti = false, ...rest }: AccordionMultiSelectProps) => (
  <Accordion variant='main' shouldRenderBody chevronSize={16} isMulti={isMulti} padding={[1.25, 2]} {...rest}>
    {children}
  </Accordion>
);

AccordionMultiSelect.Item = AccordionMultiSelectItem;
